import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  MessageSquare,
  ClipboardList,
  Calendar,
  Euro,
  MapPin,
  User,
  Clock,
  ChevronDown,
  ChevronUp,
  Send,
  Pause,
  Play,
  Users,
} from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  getBackendBaseUrl,
  setConversationActive,
} from "@/lib/orcaBackend";

interface ConversationMessage {
  role: string;
  content: string;
  datetime?: string;
}

interface Conversation {
  id: string;
  client_id: string;
  budget_eur: number;
  start_date: string;
  end_date: string;
  destination: string;
  summary: string;
  transcript: ConversationMessage[];
  closing_probability: number;
  last_updated: string;
  broker_notes: string;
  status: string;
  active: boolean;
  num_guests: number;
  clients?: {
    full_name: string;
    email: string;
    phone: string;
    preferred_budget_eur: number;
    taste_notes: string;
  };
}

interface ChatModalProps {
  conversation: Conversation;
  onClose: () => void;
  onConversationUpdated?: (conversation: Conversation) => void;
}

export const ChatModal = ({
  conversation,
  onClose,
  onConversationUpdated,
}: ChatModalProps) => {
  const [isAdditionalRequestsOpen, setIsAdditionalRequestsOpen] =
    useState(false);
  const [messageInput, setMessageInput] = useState("");
  const [isAgentActive, setIsAgentActive] = useState(conversation.active);
  const queryClient = useQueryClient();
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { profile, session } = useAuth();

  useEffect(() => {
    setIsAgentActive(conversation.active);
  }, [conversation.id, conversation.active]);

  // Extract messages from the transcript array and filter out tool messages
  const allMessages = conversation.transcript || [];
  const messages = allMessages.filter((message) => message.role !== "tool");

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollElement = scrollAreaRef.current.querySelector(
        "[data-radix-scroll-area-viewport]"
      );
      if (scrollElement) {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }
    }
  }, [messages]);

  const toggleAgentMutation = useMutation({
    mutationFn: async (newStatus: boolean) => {
      return setConversationActive({
        conversationId: conversation.id,
        active: newStatus,
        accessToken: session?.access_token,
        fallbackBackendUrl: profile?.ngrok_url,
      }) as Promise<Conversation>;
    },
    onMutate: (newStatus) => {
      setIsAgentActive(newStatus);
    },
    onSuccess: (updatedConversation) => {
      setIsAgentActive(updatedConversation.active);
      onConversationUpdated?.(updatedConversation);
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
      queryClient.invalidateQueries({ queryKey: ["all-conversations"] });
    },
    onError: (error) => {
      console.error("Failed to toggle agent status:", error);
      setIsAgentActive(conversation.active);
      alert(
        error instanceof Error
          ? error.message
          : "Failed to update agent status. Please refresh and try again."
      );
    },
  });

  const sendMessageMutation = useMutation({
    mutationFn: async (message: string) => {
      console.log("Sending message:", {
        conversation_id: conversation.id,
        message,
      });

      // Invalidate any cached profile data to force fresh fetch
      queryClient.invalidateQueries({ queryKey: ["profile"] });

      // Get fresh profile data to ensure we have the latest backend URL fallback.
      const { data: freshProfile, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", session?.user?.id)
        .single();

      if (profileError) {
        console.error("Error fetching fresh profile:", profileError);
        throw new Error("Failed to fetch profile data");
      }

      // Legacy profile field can hold the stable backend URL fallback.
      const profileWithNgrok = freshProfile as typeof freshProfile & {
        ngrok_url?: string | null;
      };

      console.log("Fresh profile data:", profileWithNgrok);
      console.log("Fresh profile backend URL fallback:", profileWithNgrok?.ngrok_url);

      const backendBaseUrl = getBackendBaseUrl(profileWithNgrok?.ngrok_url);

      if (!backendBaseUrl) {
        throw new Error(
          "Backend URL is not configured for your account. Please contact support."
        );
      }
      console.log("Using backend URL:", backendBaseUrl);
      console.log(
        "Full URL being fetched:",
        `${backendBaseUrl}/api/send-message`
      );

      const response = await fetch(`${backendBaseUrl}/api/send-message`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: session ? `Bearer ${session.access_token}` : "",
        },
        body: JSON.stringify({
          conversation_id: conversation.id,
          message: message,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Send message API error:", response.status, errorText);
        throw new Error(
          `Failed to send message: ${response.status} ${errorText}`
        );
      }

      const result = await response.json();
      console.log("Send message API response:", result);
      return result;
    },
    onSuccess: async () => {
      console.log("Message sent successfully");
      setMessageInput("");

      // Fetch updated conversation data from database to get the new transcript
      const { data: updatedConversation, error } = await supabase
        .from("conversations")
        .select(
          `
          *,
          clients (
            full_name,
            email,
            phone,
            preferred_budget_eur,
            taste_notes
          )
        `
        )
        .eq("id", conversation.id)
        .single();

      if (!error && updatedConversation) {
        // Parse the updated transcript
        let parsedTranscript: ConversationMessage[] = [];

        if (updatedConversation.transcript) {
          try {
            if (Array.isArray(updatedConversation.transcript)) {
              parsedTranscript = updatedConversation.transcript.map(
                (msg: unknown) => ({
                  role: (msg as ConversationMessage).role || "",
                  content: (msg as ConversationMessage).content || "",
                  datetime: (msg as ConversationMessage).datetime,
                })
              );
            } else if (typeof updatedConversation.transcript === "string") {
              const parsed = JSON.parse(updatedConversation.transcript);
              if (Array.isArray(parsed)) {
                parsedTranscript = parsed.map((msg: unknown) => ({
                  role: (msg as ConversationMessage).role || "",
                  content: (msg as ConversationMessage).content || "",
                  datetime: (msg as ConversationMessage).datetime,
                }));
              }
            }
          } catch (e) {
            console.error("Error parsing updated transcript:", e);
          }
        }

        // Update the local transcript state
        conversation.transcript = parsedTranscript;
      }

      // Also refresh the main conversations data
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
    onError: (error) => {
      alert(error.message || "Failed to send message.");
    },
  });

  const handleToggleAgent = (checked: boolean) => {
    toggleAgentMutation.mutate(checked);
  };

  const handleSendMessage = () => {
    if (messageInput.trim() && !isAgentActive) {
      sendMessageMutation.mutate(messageInput.trim());
    }
  };

  const handleClearCache = () => {
    // Clear all React Query cache
    queryClient.clear();
    // Force refresh the page
    window.location.reload();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB"); // dd/mm/yyyy format
    } catch (e) {
      return dateString;
    }
  };

  const formatMessageTimestamp = (datetime?: string) => {
    if (!datetime) return "";
    try {
      const date = new Date(datetime);
      return (
        date.toLocaleDateString("en-GB") +
        " " +
        date.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
        })
      );
    } catch (e) {
      return datetime;
    }
  };

  const getAdditionalRequests = () => {
    return (
      conversation.clients?.taste_notes ||
      conversation.summary ||
      "No additional requests specified."
    );
  };

  const additionalRequests = getAdditionalRequests();
  const shouldCollapse = additionalRequests.length > 150;
  const oncaBrokerNotes =
    conversation.broker_notes ||
    "Onca has not generated a broker brief yet. It will appear after the next client exchange.";
  const closingProbability =
    typeof conversation.closing_probability === "number"
      ? Math.round(
          conversation.closing_probability > 1
            ? conversation.closing_probability
            : conversation.closing_probability * 100
        )
      : null;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="flex h-[92dvh] w-[calc(100vw-1rem)] max-w-6xl flex-col border border-slate-200/50 bg-gradient-to-br from-slate-50 to-slate-100 p-3 shadow-2xl sm:p-6 lg:h-[85vh]">
        <DialogHeader className="flex-shrink-0 border-b border-slate-200/70 bg-gradient-to-r from-slate-50 to-slate-100 pb-3 sm:pb-4">
          <DialogTitle className="flex items-center space-x-3 text-lg font-bold text-slate-800 sm:text-xl">
            <MessageSquare className="h-5 w-5 shrink-0 text-emerald-600 sm:h-6 sm:w-6" />
            <span className="truncate">Chat with {conversation.clients?.full_name || "Client"}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid flex-1 grid-cols-1 gap-4 overflow-y-auto py-3 lg:min-h-0 lg:grid-cols-4 lg:gap-6 lg:overflow-hidden lg:py-4">
          {/* Chat Summary Panel */}
          <div className="rounded-lg border border-slate-200/50 bg-white/60 p-4 shadow-lg backdrop-blur-sm lg:col-span-1 lg:overflow-y-auto">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
              <MessageSquare className="h-4 w-4 mr-2 text-blue-600" />
              Chat Summary
            </h3>

            <div className="space-y-4 text-sm">
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-medium text-gray-700 mb-2">AI Summary:</h4>
                <p className="text-sm text-gray-600">
                  {conversation.summary ||
                    "No summary available yet. This will be generated automatically as the conversation progresses."}
                </p>
              </div>

              <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <h4 className="font-medium text-gray-700 mb-2 flex items-center">
                  <ClipboardList className="h-4 w-4 mr-2 text-emerald-700" />
                  Onca Broker Brief
                </h4>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {oncaBrokerNotes}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 rounded-lg bg-slate-50 border border-slate-200">
                  <label className="text-xs font-medium text-gray-500">
                    Status
                  </label>
                  <p className="text-sm font-semibold text-gray-800">
                    {conversation.status || "discovery"}
                  </p>
                </div>
                <div className="p-2 rounded-lg bg-slate-50 border border-slate-200">
                  <label className="text-xs font-medium text-gray-500">
                    Close Signal
                  </label>
                  <p className="text-sm font-semibold text-gray-800">
                    {closingProbability !== null
                      ? `${closingProbability}%`
                      : "TBD"}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2 text-gray-600">
                <Clock className="h-4 w-4 text-blue-600" />
                <div>
                  <label className="font-medium">Last Contact:</label>
                  <p className="text-gray-800">
                    {conversation.last_updated
                      ? formatDate(conversation.last_updated)
                      : "N/A"}
                  </p>
                </div>
              </div>

              <div>
                <label className="font-medium text-gray-600">
                  Total Messages:
                </label>
                <p className="text-gray-800">{messages.length}</p>
              </div>
            </div>
          </div>

          {/* Client Details Panel */}
          <div className="rounded-lg border border-slate-200/50 bg-white/60 p-4 shadow-lg backdrop-blur-sm lg:col-span-1 lg:overflow-y-auto">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
              <User className="h-4 w-4 mr-2 text-blue-600" />
              Client Details
            </h3>

            <div className="space-y-3 text-sm">
              <div>
                <label className="font-medium text-gray-600">Name:</label>
                <p className="text-gray-800">
                  {conversation.clients?.full_name || "Not provided"}
                </p>
              </div>

              <div>
                <label className="font-medium text-gray-600">Email:</label>
                <p className="text-gray-800">
                  {conversation.clients?.email || "Not provided"}
                </p>
              </div>

              <div>
                <label className="font-medium text-gray-600">Phone:</label>
                <p className="text-gray-800">
                  {conversation.clients?.phone || "Not provided"}
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <Euro className="h-4 w-4 text-green-600" />
                <div>
                  <label className="font-medium text-gray-600">Budget:</label>
                  <p className="text-gray-800">
                    {conversation.budget_eur
                      ? `€${conversation.budget_eur.toLocaleString()}`
                      : "TBD"}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-blue-600" />
                <div>
                  <label className="font-medium text-gray-600">
                    Travel Dates:
                  </label>
                  <p className="text-gray-800">
                    {conversation.start_date && conversation.end_date
                      ? `${formatDate(conversation.start_date)} - ${formatDate(
                          conversation.end_date
                        )}`
                      : "Not specified"}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <MapPin className="h-4 w-4 text-purple-600" />
                <div>
                  <label className="font-medium text-gray-600">
                    Destination:
                  </label>
                  <p className="text-gray-800">
                    {conversation.destination || "Not specified"}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Users className="h-4 w-4 text-blue-600" />
                <div>
                  <label className="font-medium text-gray-600">
                    Number of Guests:
                  </label>
                  <p className="text-gray-800">
                    {conversation.num_guests || "Not specified"}
                  </p>
                </div>
              </div>

              {/* Additional Requests Section */}
              <div className="mt-4 pt-3 border-t border-gray-200">
                <label className="font-medium text-gray-600 mb-2 block">
                  Additional Requests:
                </label>
                {shouldCollapse ? (
                  <Collapsible
                    open={isAdditionalRequestsOpen}
                    onOpenChange={setIsAdditionalRequestsOpen}
                  >
                    <CollapsibleTrigger className="flex items-center justify-between w-full text-left">
                      <span className="text-sm text-gray-800">
                        {additionalRequests.substring(0, 100)}...
                      </span>
                      {isAdditionalRequestsOpen ? (
                        <ChevronUp className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                      )}
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-2">
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">
                        {additionalRequests}
                      </p>
                    </CollapsibleContent>
                  </Collapsible>
                ) : (
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">
                    {additionalRequests}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Chat Transcript */}
          <div className="flex min-h-[520px] flex-col rounded-lg border border-slate-200/50 bg-white/60 shadow-lg backdrop-blur-sm lg:col-span-2 lg:min-h-0">
            <div className="flex-shrink-0 rounded-t-lg border-b border-slate-200/70 bg-gradient-to-r from-slate-50/80 to-slate-100/80 p-4">
              <h3 className="font-semibold text-slate-800">Chat Transcript</h3>
              <p className="text-xs text-slate-500 mt-1">
                {messages.length > 0
                  ? `${messages.length} messages`
                  : "No messages yet"}
              </p>
            </div>

            <div className="flex-1 min-h-0">
              <ScrollArea ref={scrollAreaRef} className="h-full">
                <div className="p-4 space-y-4 flex flex-col justify-end min-h-full">
                  {messages.length > 0 ? (
                    messages.map((message, index) => (
                      <div
                        key={index}
                        className={`flex ${
                          message.role === "assistant"
                            ? "justify-end"
                            : "justify-start"
                        }`}
                      >
                        <div
                          className={`max-w-[92%] rounded-lg p-3 shadow-sm sm:max-w-[80%] ${
                            message.role === "assistant"
                              ? "bg-emerald-50/80 text-emerald-900 border border-emerald-200/50"
                              : "bg-slate-100/80 text-slate-800 border border-slate-200/50"
                          }`}
                        >
                          <div className="text-xs font-medium mb-1 opacity-75">
                            {message.role === "assistant"
                              ? "Orca AI"
                              : "Client"}
                          </div>
                          <p className="text-sm whitespace-pre-wrap">
                            {message.content}
                          </p>
                          {message.datetime && (
                            <div className="text-xs opacity-60 mt-1">
                              {formatMessageTimestamp(message.datetime)}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex items-center justify-center h-full text-slate-500">
                      <div className="text-center">
                        <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>No chat transcript available</p>
                        <p className="text-xs mt-1">
                          Messages will appear here once the conversation begins
                        </p>
                        <div className="mt-3 p-3 bg-amber-50/80 border border-amber-200/50 rounded-lg">
                          <p className="text-xs text-amber-700">
                            Debug: Looking for messages in 'transcript' array
                          </p>
                          <p className="text-xs text-amber-700 mt-1">
                            Filtered messages: {messages.length}, Original:{" "}
                            {allMessages.length}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>

            {/* Message Input Section */}
            <div className="flex-shrink-0 rounded-b-lg border-t border-slate-200/70 bg-gradient-to-r from-slate-50/80 to-slate-100/80 p-4">
              {!isAgentActive && (
                <p className="text-xs text-orange-600 mb-3">
                  Pause the agent to send manual messages to the client
                </p>
              )}

              <div className="mb-3 flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={isAgentActive}
                    onCheckedChange={handleToggleAgent}
                    disabled={toggleAgentMutation.isPending}
                    className="data-[state=checked]:bg-emerald-600"
                  />
                  <span className="text-sm font-medium text-slate-700">
                    Agent {isAgentActive ? "Active" : "Paused"}
                  </span>
                  {isAgentActive ? (
                    <Play className="h-4 w-4 text-green-600" />
                  ) : (
                    <Pause className="h-4 w-4 text-orange-600" />
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  placeholder={
                    isAgentActive
                      ? "Pause the agent to send messages"
                      : "Type your message..."
                  }
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={isAgentActive || sendMessageMutation.isPending}
                  className="flex-1"
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={
                    isAgentActive ||
                    !messageInput.trim() ||
                    sendMessageMutation.isPending
                  }
                  size="sm"
                  className="px-3"
                >
                  {sendMessageMutation.isPending ? (
                    "Sending..."
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
